import React, { useContext, useState, useEffect, SyntheticEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import Header from "../components/Header";
import Footer from "../components/Footer";
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { RuntimeValuesContext } from '../context/RuntimeValuesContext';
import { useTranslation } from 'react-i18next';
import config from '../config/config.json';
import ImageWithFallback from '../components/ImageWithFallback';

// Interfaces for our data structures
interface Product {
    id: number;
    productType: string;
    productBrand: string;
    name: string;
    price: number;
    priceCurrency: number;
    photoMain: string;
    sku: string;
    brandPhotoMain: string;
}

interface Brand {
    id: number;
    name: string;
    photoMain: string;
}

const ProductsV2: React.FC = () => {
    const { t } = useTranslation("global");
    const { runtimeValues } = useContext(RuntimeValuesContext);
    const location = useLocation();

    // --- STATE MANAGEMENT ---
    const [products, setProducts] = useState<Product[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [currentItems, setCurrentItems] = useState<Product[]>([]);
    const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set(['All']));
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);

    // Pagination state
    const [pageCount, setPageCount] = useState<number>(0);
    const [itemOffset, setItemOffset] = useState<number>(0);
    const itemsPerPage = 15;

    // SEO Title State
    const [title, setTitle] = useState<string>(`${t("plp.section3Title")} ${t("seo.compl")} ${runtimeValues.country} - wicaglobalgear.com`);

    // --- DATA FETCHING (Decoupled for performance) ---
    useEffect(() => {
        const fetchPageData = async () => {
            setIsLoadingProducts(true); // Show spinner for products
            try {
                // Fetch brands first (fast call)
                const brandsResponse = await axios.get(`${config.backend.url}/api/productbrands`);
                setBrands(brandsResponse.data);

                // Fetch products in parallel (slow call)
                const productsResponse = await axios.get(`${config.backend.url}/api/products`, {
                    params: { currency: runtimeValues.currency }
                });
                setProducts(productsResponse.data);

            } catch (error) {
                console.error('Error fetching page data:', error);
            } finally {
                setIsLoadingProducts(false); // Hide spinner for products
            }
        };

        fetchPageData();
    }, [runtimeValues.currency]);

    // --- FILTERING & PAGINATION LOGIC ---
    useEffect(() => {
        const productsToFilter = selectedBrands.has('All') || selectedBrands.size === 0
            ? [...products]
            : products.filter(product => selectedBrands.has(product.productBrand));
        
        setFilteredProducts(productsToFilter);
        setItemOffset(0); // Reset to first page on filter change
    }, [products, selectedBrands]);

    useEffect(() => {
        const endOffset = itemOffset + itemsPerPage;
        setCurrentItems(filteredProducts.slice(itemOffset, endOffset));
        setPageCount(Math.ceil(filteredProducts.length / itemsPerPage));
    }, [itemOffset, itemsPerPage, filteredProducts]);
    
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const brandFromUrl = params.get('brand');
        if (brandFromUrl && brands.length > 0) {
            setSelectedBrands(new Set([brandFromUrl]));
        }
    }, [location.search, brands]);

    // --- EVENT HANDLERS ---
    const handleBrandSelection = (brandName: string) => {
        const newSelectedBrands = new Set<string>();
        if (brandName === 'All') {
            newSelectedBrands.add('All');
        } else {
            newSelectedBrands.add(brandName);
        }
        setSelectedBrands(newSelectedBrands);
    };

    const handleCheckboxBrandSelection = (brandName: string) => {
        const newSelectedBrands = new Set(selectedBrands);
        if (brandName === 'All') {
            newSelectedBrands.clear();
            newSelectedBrands.add('All');
        } else {
            newSelectedBrands.delete('All');
            if (newSelectedBrands.has(brandName)) {
                newSelectedBrands.delete(brandName);
            } else {
                newSelectedBrands.add(brandName);
            }
            if (newSelectedBrands.size === 0) {
                newSelectedBrands.add('All');
            }
        }
        setSelectedBrands(newSelectedBrands);
    };

    const handlePageClick = (event: { selected: number }) => {
        const newOffset = (event.selected * itemsPerPage) % filteredProducts.length;
        setItemOffset(newOffset);
    };

    // --- UI HELPERS ---
    const getBrandLogoStyle = (brandName: string) => {
        return selectedBrands.has(brandName) ? "plpBrand plpBrandSel" : "plpBrand";
    };
    
    let formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    switch(runtimeValues.currency){
        case 'COP': formatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }); break;
        case 'CLP': formatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }); break;
        case 'PEN': formatter = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }); break;
        case 'PYG': formatter = new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }); break;
        case 'UYU': formatter = new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }); break;
        default: formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }

    return (
        <>
            <Helmet><title>{title}</title></Helmet>
            <div className="app-container">
                <Header />
                <div className="app-content">
                    <div className="app-content-left">
                        <div className="plpBrandContainer">
                            <section>
                                <header className="plpBrandHeader">
                                    <h2 className="plpBrandHeaderText">{t("plp.filterTitle")}</h2>
                                </header>      
                                <div className="divFilter">
                                    <span className="spanFilter">{t("plp.filterBrand")}</span>
                                    <ul>
                                        <li className="checkboxFilter">
                                            <input type="checkbox" checked={selectedBrands.has('All')} onChange={() => handleCheckboxBrandSelection('All')} /> All
                                        </li>
                                        {brands.map((brand) => (
                                            <li className="checkboxFilter" key={brand.id}>
                                                <input type="checkbox" checked={selectedBrands.has(brand.name)} onChange={() => handleCheckboxBrandSelection(brand.name)} /> {brand.name}
                                            </li>
                                        ))}
                                    </ul> 
                                    <Link to="https://www.red.com/locations-all#dealers" target='_blank' className="header-logo" title="Authorized Dealer">
                                        <img className="imgDistributor" alt='Authorized Dealer' src="images/dealer_coin.png"></img>
                                    </Link>                                                 
                                </div>                                              
                            </section>                      
                        </div>
                    </div>
                    <div className="app-content-right">
                        <div className="plpBrandContainer">
                            <section>
                                <header className="plpBrandHeader">
                                    <h2 className="plpBrandHeaderText">{t("plp.section1Title")}</h2>
                                </header>
                                <div className="plpBrandContainerDetail">
                                    {brands.map((brand) => (
                                        <div key={brand.id} className={getBrandLogoStyle(brand.name)} onClick={() => handleBrandSelection(brand.name)}>
                                            <img src={`images/${brand.photoMain}`} className="plpBrandImage" alt={brand.name} />
                                            <span className="plpBrandName">{brand.name.toUpperCase()}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>

                        <div className="plpBrandContainer">
                            <section>
                                <header className="plpBrandHeader">
                                    <h2 className="plpBrandHeaderText">{t("plp.filterResultP1")} {isLoadingProducts ? '' : filteredProducts.length} {t("plp.filterResultP2")}</h2>
                                </header>
                                
                                {isLoadingProducts ? (
                                    <div className="loading-spinner-container">
                                        <div className="loading-spinner"></div>
                                        <p>Loading Products...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="plpBrandContainerDetail">
                                            <div className="plpProduct-list">
                                                {currentItems.map((product) => {
                                                    const primaryImagePath = product.photoMain ? `images/${product.photoMain}` : null;
                                                    const fallbackImagePath = product.brandPhotoMain ? `images/${product.brandPhotoMain}` : null;
                                                    const imageForCart = product.photoMain || product.brandPhotoMain || '';

                                                    return (
                                                        <div className="productListProductCard" key={product.id}>
                                                            <ImageWithFallback primarySrc={primaryImagePath} fallbackSrc={fallbackImagePath} alt={product.name} loading="lazy" />
                                                            <h4 className='productDetailName'>{product.name}</h4>
                                                            <p className='productDetailPrice'>{formatter.format(product.priceCurrency)}</p>
                                                            <Link to={`/productDetail?productId=${product.id}`} className="plpButton">{t("general.viewMore")}</Link>
                                                            <Link to={`/cart?productId=${product.id}&productName=${encodeURIComponent(product.name)}&productImage=${encodeURIComponent(product.photoMain || '')}&fallbackImage=${encodeURIComponent(product.brandPhotoMain || '')}&productPrice=${product.price}`} className="plpButtonFocus">{t("general.addCart")}</Link>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="carouselButtons">
                                            <ReactPaginate
                                                containerClassName='paginationContainer'
                                                pageLinkClassName='paginationButton'
                                                previousLinkClassName='paginationButton'
                                                nextLinkClassName='paginationButton'
                                                activeLinkClassName='paginationButtonFocus'
                                                breakLabel="..."
                                                nextLabel=">"
                                                previousLabel="<"
                                                onPageChange={handlePageClick}
                                                pageRangeDisplayed={2}
                                                marginPagesDisplayed={1}
                                                pageCount={pageCount}
                                                renderOnZeroPageCount={null}
                                            />
                                        </div>
                                    </>
                                )}
                            </section>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default ProductsV2;